import { computed as pcomputed, signal as psignal } from '@preact/signals-core';
export function generateSignalId() {
    return 'signal:xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function generateRevisionId() {
    return Math.random().toString(36).slice(2);
}
function toPublicSignal(signal) {
    return {
        ...(signal.readonly ? {} : { set: signal.set }),
        get: signal.get,
        peek: signal.peek,
        subscribe: signal.subscribe,
        id: signal.id,
        isSignal: true,
    };
}
export class SignalsRegistry {
    signalsStore = new Map();
    serviceSignalsRegistry = new Map();
    replicators = [];
    createInnerSignal(value, id, readonly = false) {
        let revisionId = generateRevisionId();
        const sig = psignal(value);
        const signal = {
            get: () => sig.value,
            peek: () => sig.peek(),
            syncSignal: (newValue, revision) => {
                if (sig.peek() !== newValue && revision !== revisionId) {
                    revisionId = revision;
                    sig.value = newValue;
                }
            },
            set: (newValue) => {
                if (sig.peek() !== newValue) {
                    revisionId = generateRevisionId();
                    sig.value = newValue;
                }
            },
            subscribe: (sfn) => sig.subscribe(sfn),
            id,
            isSignal: true,
            getRevision: () => revisionId,
            readonly,
        };
        return signal;
    }
    getSignals() {
        return Array.from(this.signalsStore.values());
    }
    clearSignalsRegistry() {
        this.signalsStore.clear();
        this.serviceSignalsRegistry.clear();
        this.replicators = [];
    }
    _createSignal(initialValue, id = generateSignalId(), readonly = false) {
        const innerSignal = this.createInnerSignal(initialValue, id, readonly);
        this.signalsStore.set(innerSignal.id, innerSignal);
        innerSignal.subscribe((newValue) => {
            this.replicators.forEach((replicator) => replicator(innerSignal.id, newValue, innerSignal.getRevision()));
        });
        return toPublicSignal(innerSignal);
    }
    setOrCreateSignal(id, initialValue, readonly = false) {
        const existingSignal = this.signalsStore.get(id);
        if (existingSignal) {
            return toPublicSignal(existingSignal);
        }
        return this._createSignal(initialValue, id, readonly);
    }
    createSignal(initialValue, id) {
        return this._createSignal(initialValue, id);
    }
    createComputedSignal(fn, id = generateSignalId()) {
        const sig = pcomputed(fn);
        const signal = {
            get: () => sig.value,
            peek: () => sig.peek(),
            id,
            subscribe: (sfn) => sig.subscribe(sfn),
            isSignal: true,
        };
        // a replica of the signal that can be used to subscribe to the computed signal and set the value
        const replicaSignal = this.createInnerSignal(sig.peek(), id, true);
        this.signalsStore.set(signal.id, replicaSignal);
        sig.subscribe((newValue) => {
            replicaSignal.set(newValue);
            this.replicators.forEach((replicator) => {
                try {
                    replicator(signal.id, newValue, replicaSignal.getRevision());
                }
                catch (e) {
                    console.error(`Error replicating signal ${signal.id}`, e);
                }
            });
        });
        return signal;
    }
    getSignalById(id) {
        return this.signalsStore.get(id);
    }
    addReplicator(replicator) {
        this.replicators.push(replicator);
    }
    getServiceSignals(serviceId) {
        return this.serviceSignalsRegistry.get(serviceId);
    }
    setServiceSignals(serviceId, signals) {
        this.serviceSignalsRegistry.set(serviceId, signals);
    }
}
export const createSignalsRegistry = () => new SignalsRegistry();
